// utils/HTMLReportGenerator/ReportUtils.js

export class ReportUtils {
  /**
   * Construire la hiérarchie tiroir > module > fibre
   */
  static buildFiberHierarchy(fibers) {
    const hierarchy = {};

    fibers.forEach((fiber) => {
      const tiroir = fiber.tiroir;
      const module = fiber.module;

      if (!hierarchy[tiroir]) {
        hierarchy[tiroir] = {};
      }
      if (!hierarchy[tiroir][module]) {
        hierarchy[tiroir][module] = [];
      }

      hierarchy[tiroir][module].push(fiber);
    });

    return hierarchy;
  }

  /**
   * Déterminer le status effectif d'une fibre en analysant ses messages
   * @param {Object} fiber - Objet fibre
   * @returns {string} "ok", "warning", ou "error"
   */
  static determineEffectiveStatus(fiber) {
    const messages = fiber.testResults?.messages || [];
    const hasErrors = messages.some((msg) => msg.type === "error");
    const hasWarnings = messages.some((msg) => msg.type === "warning");

    if (hasErrors) return "error";
    if (hasWarnings) return "warning";
    return "ok";
  }

  /**
   * Calculer les statistiques d'un tiroir
   */
  static calculateTiroirStats(modules) {
    let total = 0,
      ok = 0,
      warnings = 0,
      errors = 0;

    Object.values(modules).forEach((fibers) => {
      fibers.forEach((fiber) => {
        total++;
        const effectiveStatus = this.determineEffectiveStatus(fiber);
        if (effectiveStatus === "ok") ok++;
        else if (effectiveStatus === "warning") warnings++;
        else if (effectiveStatus === "error") errors++;
      });
    });

    return { total, ok, warnings, errors };
  }

  /**
   * Calculer les statistiques d'un module
   */
  static calculateModuleStats(fibers) {
    const { length: total } = fibers;
    let ok = 0,
      warnings = 0,
      errors = 0;

    fibers.forEach((fiber) => {
      const effectiveStatus = this.determineEffectiveStatus(fiber);
      if (effectiveStatus === "ok") ok++;
      else if (effectiveStatus === "warning") warnings++;
      else if (effectiveStatus === "error") errors++;
    });

    return { total, ok, warnings, errors };
  }

  /**
   * Calculer les statistiques pour les filtres
   */
  static calculateFilterStats(hierarchy) {
    let total = 0,
      errors = 0,
      warnings = 0;
    const rules = {};

    Object.values(hierarchy).forEach((modules) => {
      Object.values(modules).forEach((fibers) => {
        fibers.forEach((fiber) => {
          total++;
          const effectiveStatus = this.determineEffectiveStatus(fiber);
          if (effectiveStatus === "error") errors++;
          else if (effectiveStatus === "warning") warnings++;

          // Compter les règles
          fiber.testResults.messages.forEach((msg) => {
            if (!rules[msg.rule]) rules[msg.rule] = 0;
            rules[msg.rule]++;
          });
        });
      });
    });

    return { total, errors, warnings, rules };
    //return { total: errors + warnings, errors, warnings, rules };
  }

  /**
   * Générer une référence compacte pour une fibre
   */
  static generateCompactReference(fiber) {
    const portFormatted = String(fiber.port).padStart(2, "0");
    return `${fiber.tiroir}-M${fiber.module}-${portFormatted}`;
  }

  /**
   * Générer les détails d'une fibre pour treeview
   */
  static generateFiberDetails(fiber) {
    const effectiveStatus = this.determineEffectiveStatus(fiber);

    if (effectiveStatus === "ok") {
      return '<div class="ok-message">✨ Aucun problème détecté</div>';
    }

    return fiber.testResults.messages
      .map(
        (msg) =>
          `<div class="tree-message ${msg.type}">
         <div class="message-title">[${msg.rule}] ${msg.title}</div>
         <div class="message-content">${msg.message}</div>
       </div>`
      )
      .join("");
  }

  /**
   * Déterminer la classe de statut d'un conteneur
   */
  static getStatusClass(stats) {
    const hasErrors = stats.errors > 0;
    const hasWarnings = stats.warnings > 0;
    return hasErrors ? "error" : hasWarnings ? "warning" : "ok";
  }

  /**
   * Générer l'icône de statut pour une fibre
   */
  static getStatusIcon(status) {
    // Utiliser le status effectif pour l'icône
    return status === "ok" ? "🟢" : status === "warning" ? "🟡" : "🔴";
  }

  /**
   * Générer un chip d'état coloré
   */
  static generateStateChip(etat) {
    const chipClass = etat?.toLowerCase() === "libre" ? "libre" : "occupee";
    return `<span class="state-chip ${chipClass}">${etat}</span>`;
  }

  /**
   * Générer un chip BPO bleu clair pour les fibres OK
   */
  static generateBpoChip(bpo) {
    if (!bpo) return "";
    const result = `<span class="bpo-chip">${bpo}</span>`;
    return result;
  }
}
